import { useCallback, useEffect, useMemo, useState } from "react";
import {
  serviceGetChatInfo,
  serviceGetConversationDetail,
} from "../../../services/Chat.service";
import { getSocketInstance } from "../../../libs/SocketUtils";
import Constants from "../../../config/constants";
import LogUtils from "../../../libs/LogUtils";
import { useDispatch, useSelector } from "react-redux";
import { fetchEmployees } from "../../../actions/EmployeeList.action";
import { useLocation } from "react-router-dom";
import history from "../../../libs/history.utils";
import RouteName from "../../../routes/Route.name";
import EventEmitter from "../../../libs/Events.utils";

const audio = new Audio(require("../../../assets/audio/just-saying.ogg"));

function useMessagingHook() {
  const [sidePanel, setSidePanel] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [chatUser, setChatUser] = useState(null);
  const [loadingStates, setLoadingStates] = useState({
    SINGLE: false,
    GROUP: false,
  });
  const [chatInfoSingle, setChatInfoSingle] = useState([]);
  const [groupChatInfo, setGroupChatInfo] = useState([]);
  const [chatInfoLoaded, setChatInfoLoaded] = useState(false);
  const [pageState, setPageState] = useState({
    SINGLE: { page: 1, hasMore: true },
    GROUP: { page: 1, hasMore: true },
  });
  const socket = getSocketInstance();
  const { user } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const location = useLocation();
  const open = Boolean(anchorEl);
  const id = open ? "chat-popover" : undefined;

  const conversationId = useMemo(() => {
    return (
      location?.state?.notificationData?.conversationId ||
      new URLSearchParams(location?.search).get("conversationId")
    );
  }, [location]);

  useEffect(() => {
    if (location?.state?.initiateContactChat) {
      setChatUser(location?.state?.initiateContactChat);
    }
  }, []);

  useEffect(() => {
    dispatch(
      fetchEmployees(
        {
          employee_keys: [
            "department.name",
            "designation.name",
            "location.name",
          ],
        },
        user?.id
      )
    );
  }, []);

  const getConversationDetails = useCallback(async (conversationId) => {
    const res = await serviceGetConversationDetail({
      conversation_id: conversationId,
    });
    if (!res?.error) {
      return res.data;
    }
  }, []);

  const handleMessagingListUpdate = useCallback(
    async (data) => {
      const { message } = data;
      const { channel } = message;
      let chats = [];
      if (message.chat_type === "SINGLE") {
        chats = JSON.parse(JSON.stringify(chatInfoSingle));
      } else {
        chats = JSON.parse(JSON.stringify(groupChatInfo));
      }
      let index = -1;
      if (channel === Constants.SOCKET_CHANNELS.INDIVIDUAL_MESSAGE_CHANNEL) {
        index = chats.findIndex(
          (chat) => chat.conversation_id === message.conversation_id
        );
      }
      if (index >= 0) {
        chats[index].last_message_time = message.send_time;
        if (
          message.sender_id !== user.user_id &&
          window.CURRENT_SOCKET_ROOM !== message.conversation_id
        ) {
          chats[index].unread_count += 1;
        }
      } else {
        if (channel === Constants.SOCKET_CHANNELS.CONVERSATION_DELETE) {
          const existingIndex = chats.findIndex(
            (chat) => chat.conversation_id === message.conversation_id
          );
          if (existingIndex >= 0) {
            chats.splice(existingIndex, 1);
          }
        } else {
          const conversationDetails = await getConversationDetails(
            message.conversation_id
          );
          if (conversationDetails) {
            conversationDetails.last_message_time = message.send_time;
            const existingIndex = chats.findIndex(
              (chat) =>
                chat.conversation_id === conversationDetails.conversation_id
            );
            if (existingIndex >= 0) {
              chats.splice(existingIndex, 1);
            }
            chats.unshift(conversationDetails);
          }
        }
      }
      chats.sort((a, b) => b.last_message_time - a.last_message_time);
      if (message.chat_type === "SINGLE") {
        setChatInfoSingle(chats);
      } else {
        setGroupChatInfo(chats);
      }
      // if (message.sender_id !==  user.user_id && window.CURRENT_SOCKET_ROOM !== message.conversation_id) {
      //   if ([Constants.SOCKET_CHANNELS.INDIVIDUAL_MESSAGE_CHANNEL, Constants.SOCKET_CHANNELS.NEW_CONVERSATION]) {
      //     audio.play();
      //   }
      // }
    },
    [
      chatInfoSingle,
      groupChatInfo,
      setChatInfoSingle,
      setGroupChatInfo,
      getConversationDetails,
      user,
    ]
  );

  useEffect(() => {
    socket.on(
      Constants.SOCKET_EVENT.UPDATE_MESSAGE_LIST,
      handleMessagingListUpdate
    );

    return () => {
      socket.removeListener(
        Constants.SOCKET_EVENT.UPDATE_MESSAGE_LIST,
        handleMessagingListUpdate
      );
    };
  }, [handleMessagingListUpdate]);

  const resetUnreadCount = useCallback(
    (conversationId, type) => {
      let chats = [];
      if (type === "SINGLE") {
        chats = JSON.parse(JSON.stringify(chatInfoSingle));
      } else {
        chats = JSON.parse(JSON.stringify(groupChatInfo));
      }
      const index = chats.findIndex(
        (chat) => chat.conversation_id === conversationId
      );
      if (index >= 0 && chats[index].unread_count) {
        chats[index].unread_count = 0;
        EventEmitter.dispatch(EventEmitter.REFRESH_PENDING_COUNTS);
      }
      if (type === "SINGLE") {
        setChatInfoSingle(chats);
      } else {
        setGroupChatInfo(chats);
      }
    },
    [chatInfoSingle, groupChatInfo, setChatInfoSingle, setGroupChatInfo]
  );

  const getChatInfo = useCallback(
    async (type, setter, page = 1, append = false) => {
      setLoadingStates((prev) => ({ ...prev, [type]: true }));
      try {
        const res = await serviceGetChatInfo({ page, type });
        if (!res?.error) {
          const newData = res.data || [];
          setter((prev) => {
            const all = append ? [...prev, ...newData] : newData;

            const seen = new Set();
            return all?.filter((item) => {
              if (seen?.has(item?.conversation_id)) return false;
              seen?.add(item?.conversation_id);
              return true;
            });
          });

          setPageState((prev) => ({
            ...prev,
            [type]: {
              page,
              hasMore: newData.length >= 10,
            },
          }));
        }
      } catch (error) {
        console.error(`Error fetching ${type} chat info:`, error);
      } finally {
        setLoadingStates((prev) => ({ ...prev, [type]: false }));
      }
    },
    []
  );

  const handleLoadMore = (type, setter) => {
    const state = pageState[type];
    if (!state.hasMore || loadingStates[type]) return;

    const nextPage = state.page + 1;
    getChatInfo(type, setter, nextPage, true);
  };
  const fetchChatInfo = useCallback(async () => {
    setChatInfoLoaded(false);
    await Promise.all([
      getChatInfo("SINGLE", setChatInfoSingle),
      getChatInfo("GROUP", setGroupChatInfo),
    ]);
    setChatInfoLoaded(true);
  }, [getChatInfo]);

  useEffect(() => {
    fetchChatInfo();
  }, [fetchChatInfo]);

  const renderConversation = useCallback(() => {
    if (conversationId) {
      serviceGetConversationDetail({
        conversation_id: conversationId,
      }).then((res) => {
        if (!res?.error) {
          setChatUser((prev) => ({
            ...res?.data,
            prefilledMessage: prev?.prefilledMessage,
          }));
        }
      });
    }
  }, [conversationId]);

  useEffect(() => {
    if (conversationId) {
      renderConversation();
    }
  }, [conversationId]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchChatInfo();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchChatInfo]);

  useEffect(() => {
    if (chatInfoLoaded && chatUser?.conversation_id) {
      const { conversation_id, type } = chatUser;
      resetUnreadCount(conversation_id, type);
    }
  }, [chatInfoLoaded, chatUser?.conversation_id]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleUpdateUser = useCallback(
    (text) => {
      setChatUser(text);
      if (!text?.conversation_id) {
        history.replace(RouteName.MESSAGING);
      }
      handleClose();
    },
    [chatUser, handleClose]
  );

  const handleSideToggle = useCallback(() => {
    setSidePanel((e) => !e);
    handleClose();
  }, [setSidePanel, handleClose]);

  return {
    sidePanel,
    handleSideToggle,
    anchorEl,
    handleClick,
    handleClose,
    chatUser,
    handleUpdateUser,
    chatInfoSingle,
    groupChatInfo,
    open,
    id,
    setChatUser,
    fetchChatInfo,
    loadingStates,
    handleLoadMore,
    setChatInfoSingle,
    setGroupChatInfo,
    renderConversation
  };
}

export default useMessagingHook;
